/**
 * Display units: canonical value in, string out; typed string in, canonical
 * value out.
 *
 * Storage is always canonical — kg, L, °C, SG, minutes (PLAN §5, and the
 * commands package's document conventions). `settings.units` is a *display*
 * preference and never touches a document. Everything in this module is the
 * edge that translation happens at.
 *
 * The parser is deliberately forgiving (PLAN §4.2, "number inputs accept unit
 * suffixes and parse forgivingly"):
 *
 * ```
 * "500g"      → 0.5 kg      "1.2kg"     → 1.2 kg
 * "1.5oz"     → 0.0425 kg   "2 lb"      → 0.907 kg
 * "1 lb 8 oz" → 0.680 kg    "1lb8oz"    → 0.680 kg
 * "4.5"       → 4.5 kg, or 4.5 lb under US preferences — the *display* unit
 * ```
 *
 * Expression evaluation (`3 * 1.5`) is explicitly out of scope for M2.
 */

import type { MassFormat, UnitPreferences } from '@beerwright/commands';
import {
  barToPsi,
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  fluidOuncesToLitres,
  gallonsToLitres,
  gramsToKg,
  kgToGrams,
  kgToOunces,
  kgToPounds,
  kilopascalsToPsi,
  litresToFluidOunces,
  litresToGallons,
  litresToMillilitres,
  litresToQuarts,
  millilitresToLitres,
  ouncesToKg,
  poundsToKg,
  psiToBar,
  psiToKilopascals,
  quartsToLitres,
} from '@beerwright/core';
import { formatNumber } from '@/lib/format';

/**
 * What a number in the editor measures.
 *
 * `mass` and `hopMass` are separate because the preference is: American
 * homebrewers routinely weigh grain in pounds and hops in ounces, at different
 * magnitudes (PLAN §3).
 */
export type MeasureKind =
  | 'mass'
  | 'hopMass'
  | 'miscMass'
  | 'volume'
  | 'temperature'
  | 'time'
  | 'days'
  | 'percent'
  | 'plain'
  /** Volume per hour — boil-off. Same conversion as volume; the label is L/h or gal/h. */
  | 'volumeRate'
  /**
   * Gauge pressure — a regulator setting, or the pressure a fermentation step
   * is spunded at.
   *
   * **Canonical is psi**, which is the app's one non-metric canonical unit and
   * is deliberate: core's carbonation fit is a polynomial in °F and psi, every
   * published carbonation table is printed in psi, and the American regulators
   * most homebrewers own are marked in it. `settings.units.pressure` picks
   * between psi, bar and kPa for display, exactly as every other kind here.
   */
  | 'pressure';

/** A unit the parser and formatter both understand. */
interface UnitSpec {
  /** Suffix shown after the number, e.g. `kg`. */
  readonly label: string;
  /** Display value → canonical value. */
  readonly toCanonical: (value: number) => number;
  /** Canonical value → display value. */
  readonly fromCanonical: (value: number) => number;
  /** Decimal places the display value is rounded to. */
  readonly decimals: number;
}

const identity = (value: number) => value;

const UNITS = {
  // Two decimals, not three: 10 g resolution is what a brewer's scale and a
  // brewer's patience both read in kilograms. Anything finer is entered and
  // shown in grams. Gravity is the one three-decimal figure in the app, and it
  // lives in `format.ts`, not here.
  kg: { label: 'kg', toCanonical: identity, fromCanonical: identity, decimals: 2 },
  g: { label: 'g', toCanonical: gramsToKg, fromCanonical: kgToGrams, decimals: 1 },
  lb: { label: 'lb', toCanonical: poundsToKg, fromCanonical: kgToPounds, decimals: 2 },
  oz: { label: 'oz', toCanonical: ouncesToKg, fromCanonical: kgToOunces, decimals: 2 },
  l: { label: 'L', toCanonical: identity, fromCanonical: identity, decimals: 2 },
  ml: {
    label: 'mL',
    toCanonical: millilitresToLitres,
    fromCanonical: litresToMillilitres,
    decimals: 0,
  }, // prettier-ignore
  gal: { label: 'gal', toCanonical: gallonsToLitres, fromCanonical: litresToGallons, decimals: 2 },
  // The US small measure, for the volumes a gallon cannot describe: a starter's
  // acid dose, a bottle, a blend poured in a jug.
  floz: {
    label: 'fl oz',
    toCanonical: fluidOuncesToLitres,
    fromCanonical: litresToFluidOunces,
    decimals: 1,
  },
  qt: { label: 'qt', toCanonical: quartsToLitres, fromCanonical: litresToQuarts, decimals: 2 },
  c: { label: '°C', toCanonical: identity, fromCanonical: identity, decimals: 1 },
  f: {
    label: '°F',
    toCanonical: fahrenheitToCelsius,
    fromCanonical: celsiusToFahrenheit,
    decimals: 0,
  }, // prettier-ignore
  min: { label: 'min', toCanonical: identity, fromCanonical: identity, decimals: 0 },
  h: {
    label: 'h',
    toCanonical: (hours: number) => hours / 24,
    fromCanonical: (days: number) => days * 24,
    decimals: 0,
  },
  day: { label: 'd', toCanonical: identity, fromCanonical: identity, decimals: 0 },
  percent: { label: '%', toCanonical: identity, fromCanonical: identity, decimals: 1 },
  plain: { label: '', toCanonical: identity, fromCanonical: identity, decimals: 2 },
  psi: { label: 'psi', toCanonical: identity, fromCanonical: identity, decimals: 1 },
  // Regulator and spunding-valve faces are marked in tenths of a bar, so two
  // decimals is the resolution a brewer can actually dial in; a kPa face is
  // marked in whole units, so it gets none.
  bar: { label: 'bar', toCanonical: barToPsi, fromCanonical: psiToBar, decimals: 2 },
  kpa: {
    label: 'kPa',
    toCanonical: kilopascalsToPsi,
    fromCanonical: psiToKilopascals,
    decimals: 0,
  },
} as const satisfies Record<string, UnitSpec>;

export type UnitKey = keyof typeof UNITS;

/**
 * "1 lb 8 oz" / "1lb 8oz" / "1 lb 8" — the way a US grain scale reads.
 *
 * Applied only to mass-family cells; a volume cell that happens to contain
 * those tokens is still a wrong-family error, not a silent conversion.
 */
const COMPOUND_LB_OZ =
  /^([+-]?(?:\d+\.?\d*|\.\d+))\s*(?:lbs?|pounds?|#)\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*(?:oz|ounces?)?$/;

/** Suffixes the parser recognises, longest first so `kg` wins over `g`. */
const SUFFIXES: readonly (readonly [string, UnitKey])[] = [
  ['kg', 'kg'],
  ['kgs', 'kg'],
  ['kilos', 'kg'],
  ['g', 'g'],
  ['gr', 'g'],
  ['grams', 'g'],
  ['gram', 'g'],
  ['lb', 'lb'],
  ['lbs', 'lb'],
  ['pound', 'lb'],
  ['pounds', 'lb'],
  ['#', 'lb'],
  ['oz', 'oz'],
  ['ounce', 'oz'],
  ['ounces', 'oz'],
  ['l', 'l'],
  ['ltr', 'l'],
  ['litre', 'l'],
  ['litres', 'l'],
  ['liter', 'l'],
  ['liters', 'l'],
  ['ml', 'ml'],
  ['fl oz', 'floz'],
  ['floz', 'floz'],
  ['fluid ounce', 'floz'],
  ['fluid ounces', 'floz'],
  ['gal/h', 'gal'],
  ['gph', 'gal'],
  ['gal', 'gal'],
  ['gallon', 'gal'],
  ['gallons', 'gal'],
  ['l/h', 'l'],
  ['lph', 'l'],
  ['qt', 'qt'],
  ['quart', 'qt'],
  ['quarts', 'qt'],
  ['c', 'c'],
  ['°c', 'c'],
  ['celsius', 'c'],
  ['f', 'f'],
  ['°f', 'f'],
  ['fahrenheit', 'f'],
  ['min', 'min'],
  ['mins', 'min'],
  ['m', 'min'],
  ['hours', 'h'],
  ['hour', 'h'],
  ['hrs', 'h'],
  ['hr', 'h'],
  ['h', 'h'],
  ['d', 'day'],
  ['day', 'day'],
  ['days', 'day'],
  ['%', 'percent'],
  ['psi', 'psi'],
  ['bar', 'bar'],
  ['bars', 'bar'],
  ['kpa', 'kpa'],
  ['kilopascal', 'kpa'],
  ['kilopascals', 'kpa'],
];

/** Which unit family a suffix belongs to — a `g` typed into a volume cell is a mistake, not a unit. */
const FAMILY: Readonly<
  Record<UnitKey, 'mass' | 'volume' | 'temperature' | 'time' | 'days' | 'pressure' | 'scalar'>
> = {
  kg: 'mass',
  g: 'mass',
  lb: 'mass',
  oz: 'mass',
  l: 'volume',
  ml: 'volume',
  gal: 'volume',
  floz: 'volume',
  qt: 'volume',
  c: 'temperature',
  f: 'temperature',
  min: 'time',
  h: 'days',
  day: 'days',
  percent: 'scalar',
  plain: 'scalar',
  psi: 'pressure',
  bar: 'pressure',
  kpa: 'pressure',
};

/** The unit a kind is displayed in, given the workspace's preferences. */
export function displayUnit(kind: MeasureKind, units: UnitPreferences): UnitKey {
  switch (kind) {
    case 'mass':
      return units.mass === 'us' ? 'lb' : 'kg';
    case 'hopMass':
      return units.hopMass === 'us' ? 'oz' : 'g';
    case 'miscMass':
      return 'g';
    case 'volume':
    case 'volumeRate':
      return units.volume === 'us' ? 'gal' : 'l';
    case 'temperature':
      return units.temperature === 'fahrenheit' ? 'f' : 'c';
    case 'time':
      return 'min';
    case 'days':
      return 'day';
    case 'percent':
      return 'percent';
    case 'plain':
      return 'plain';
    case 'pressure':
      return units.pressure === 'bar' ? 'bar' : units.pressure === 'kpa' ? 'kpa' : 'psi';
  }
}

/**
 * One named unit's suffix, without going through a kind.
 *
 * For the few callers that hold a unit rather than a preference — the pressure
 * readouts, which are handed `psi | bar | kpa` straight from
 * `settings.units.pressure` and print both a primary and a cross-check figure.
 */
export function unitKeyLabel(unit: UnitKey): string {
  return UNITS[unit].label;
}

/**
 * A canonical value as the bare number one named unit shows.
 *
 * {@link formatQuantity} without the kind, for the same callers
 * {@link unitKeyLabel} exists for. Empty string for a value that is not finite,
 * matching `formatQuantity`.
 */
export function formatInUnit(canonical: number, unit: UnitKey): string {
  const spec = UNITS[unit];
  const display = spec.fromCanonical(canonical);
  if (!Number.isFinite(display)) return '';
  return trimZeros(display.toFixed(spec.decimals));
}

/**
 * A value in one named unit as its canonical value — `1 gal` → `3.785`.
 *
 * The inverse of {@link formatInUnit}, for the callers that build a compound
 * unit out of two of these (the priming rate's grams *per gallon*).
 */
export function canonicalInUnit(display: number, unit: UnitKey): number {
  return UNITS[unit].toCanonical(display);
}

/** The suffix rendered next to a kind's numbers, e.g. `kg`, `°F`, `min`. */
export function unitLabel(kind: MeasureKind, units: UnitPreferences, unit?: UnitKey): string {
  if (kind === 'volumeRate' && unit === undefined) return units.volume === 'us' ? 'gal/h' : 'L/h';
  return UNITS[unit ?? displayUnit(kind, units)].label;
}

/**
 * Dry-hop contact is stored as days. Whole days display as days; anything else
 * (a 12-hour hop, a 36-hour biotransformation) displays as hours.
 */
export function contactDisplayUnit(days: number | null): 'day' | 'h' {
  if (days === null || days <= 0) return 'day';
  const wholeDays = Math.abs(days - Math.round(days)) < 1e-9;
  return wholeDays ? 'day' : 'h';
}

/** `3 d` or `12 h`, for brew sheets, share pages and the calendar. */
export function formatContact(days: number): string {
  const unit = contactDisplayUnit(days);
  const spec = UNITS[unit];
  return `${trimZeros(spec.fromCanonical(days).toFixed(spec.decimals))} ${spec.label}`;
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Minutes as a brewer reads them: `90 min` under a day, `1 d 12 h` from a day
 * up.
 *
 * The day is the threshold, not the hour, on purpose. A 90-minute boil and a
 * 120-minute mash are said in minutes by everyone who does them, and a sheet
 * that turned one into `2 h` next to a `60 min` mash would read as two
 * different columns. Misc additions are the one place a time can run to
 * days (the schema allows a fortnight), and there `2880 min` is a number to
 * work out rather than a fact to read. Zero parts are dropped; a remainder
 * below the hour is kept, because a sheet that rounded it away would say
 * something the recipe does not.
 */
export function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  if (whole < MINUTES_PER_DAY) return `${formatNumber(whole, 0)} ${UNITS.min.label}`;
  const days = Math.floor(whole / MINUTES_PER_DAY);
  const hours = Math.floor((whole % MINUTES_PER_DAY) / 60);
  const rest = whole % 60;
  const parts = [`${formatNumber(days, 0)} ${UNITS.day.label}`];
  if (hours > 0) parts.push(`${formatNumber(hours, 0)} ${UNITS.h.label}`);
  if (rest > 0) parts.push(`${formatNumber(rest, 0)} ${UNITS.min.label}`);
  return parts.join(' ');
}

/** Options for {@link formatQuantity}. */
export interface FormatQuantityOptions {
  readonly decimals?: number;
  /**
   * How US grain mass is written. Ignored unless `kind` is `mass` and
   * `units.mass` is `us`. Defaults to decimal pounds so existing callers keep
   * their current string.
   */
  readonly massFormat?: MassFormat;
  /**
   * Override the kind's display unit for format and for a bare-number parse.
   * Dry-hop contact uses this to show hours when the stored value is not a
   * whole day.
   */
  readonly unit?: UnitKey;
}

/**
 * Canonical value → the string an input shows.
 *
 * Trailing zeros are trimmed so a 4 kg addition reads `4`, not `4.000`, but the
 * cell width is fixed by the layout so trimming cannot shift anything. Under
 * `massFormat: 'lbOz'` the string includes the unit labels ("1 lb 8 oz")
 * because two numbers share the cell.
 */
export function formatQuantity(
  canonical: number,
  kind: MeasureKind,
  units: UnitPreferences,
  options: FormatQuantityOptions = {},
): string {
  if (kind === 'mass' && units.mass === 'us' && options.massFormat === 'lbOz') {
    return formatPoundsOunces(canonical);
  }
  const spec = UNITS[options.unit ?? displayUnit(kind, units)];
  const decimals = options.decimals ?? spec.decimals;
  const display = spec.fromCanonical(canonical);
  if (!Number.isFinite(display)) return '';
  return trimZeros(display.toFixed(decimals));
}

/**
 * Canonical value → the *number* the brewer sees, converted and rounded exactly
 * as {@link formatQuantity} would print it.
 *
 * For comparing a value against a bound when the comparison has to agree with
 * what is on screen. Canonical units are °C, and °F is shown to the whole
 * degree, so a yeast published at 18–22 °C reads "64–72 °F" while 64 °F is
 * really 17.8 °C. Comparing canonically then calls a brewer who typed the
 * larger number the app just showed them out of range. Comparing what is
 * displayed cannot disagree with the display.
 *
 * Not for arithmetic — rounding twice is how a total stops matching its parts.
 */
export function displayedQuantity(
  canonical: number,
  kind: MeasureKind,
  units: UnitPreferences,
  options: FormatQuantityOptions = {},
): number {
  const spec = UNITS[options.unit ?? displayUnit(kind, units)];
  const decimals = options.decimals ?? spec.decimals;
  const factor = 10 ** decimals;
  return Math.round(spec.fromCanonical(canonical) * factor) / factor;
}

/**
 * {@link formatQuantity} plus the kind's unit label, except when the quantity
 * string already carries its units (lb+oz grain masses).
 */
export function formatQuantityWithUnit(
  canonical: number,
  kind: MeasureKind,
  units: UnitPreferences,
  options: FormatQuantityOptions = {},
): string {
  const quantity = formatQuantity(canonical, kind, units, options);
  if (quantity.length === 0) return '';
  if (kind === 'mass' && units.mass === 'us' && options.massFormat === 'lbOz') {
    return quantity;
  }
  const label = UNITS[options.unit ?? displayUnit(kind, units)].label;
  return label.length === 0 ? quantity : `${quantity} ${label}`;
}

/**
 * The ratios an equipment profile is calibrated in.
 *
 * Not units in {@link UNITS}' sense — each is a quotient of two of them, and
 * which pair is displayed depends on two preferences at once (a brewer who
 * weighs in pounds but measures in litres still gets L/kg, because "L/lb" is
 * not a figure anyone recites). They live here rather than in the components
 * that print them so that the label, the formatter and the parser cannot drift
 * apart — a field labelled qt/lb that parses its number as L/kg is a
 * 2.09× error in strike water.
 *
 * `temperaturePerHour` is a temperature **difference**, so it scales by 1.8 and
 * never shifts by 32: a mash losing 1 °C an hour loses 1.8 °F an hour, not 34.
 */
export type RatioKind =
  /** Mash thickness, grain absorption: `L/kg`, or `qt/lb` under US mass+volume. */
  | 'volumePerGrainMass'
  /** Hop absorption: `mL/g`, or `fl oz/oz` when both hop mass and volume are US. */
  | 'volumePerHopMass'
  /** Mash standing loss: `°C/h`, or `°F/h`. */
  | 'temperaturePerHour';

/** Canonical → display factor, and the decimals, for each ratio. */
const RATIOS: Readonly<
  Record<
    RatioKind,
    {
      readonly us: (units: UnitPreferences) => boolean;
      readonly metricLabel: string;
      readonly usLabel: string;
      readonly factor: number;
      readonly decimals: number;
    }
  >
> = {
  volumePerGrainMass: {
    us: (units) => units.volume === 'us' && units.mass === 'us',
    metricLabel: 'L/kg',
    usLabel: 'qt/lb',
    factor: litresToQuarts(1) / kgToPounds(1),
    decimals: 2,
  },
  volumePerHopMass: {
    us: (units) => units.volume === 'us' && units.hopMass === 'us',
    metricLabel: 'mL/g',
    usLabel: 'fl oz/oz',
    // mL/g → fl oz/oz: both halves are per-thousandth of the litre and the
    // kilogram, so the thousandths cancel and only the two conversions remain.
    factor: litresToFluidOunces(1) / kgToOunces(1),
    decimals: 2,
  },
  temperaturePerHour: {
    us: (units) => units.temperature === 'fahrenheit',
    metricLabel: '°C/h',
    usLabel: '°F/h',
    factor: 1.8,
    decimals: 1,
  },
};

/** The suffix a ratio is written with, e.g. `qt/lb`. */
export function ratioUnitLabel(kind: RatioKind, units: UnitPreferences): string {
  const spec = RATIOS[kind];
  return spec.us(units) ? spec.usLabel : spec.metricLabel;
}

/** A canonical ratio as the bare number its display unit shows. */
export function formatRatio(canonical: number, kind: RatioKind, units: UnitPreferences): string {
  const spec = RATIOS[kind];
  if (!Number.isFinite(canonical)) return '';
  const display = spec.us(units) ? canonical * spec.factor : canonical;
  return trimZeros(display.toFixed(spec.decimals));
}

/** {@link formatRatio} plus its unit, e.g. `1.5 L/kg`. */
export function formatRatioWithUnit(
  canonical: number,
  kind: RatioKind,
  units: UnitPreferences,
): string {
  const text = formatRatio(canonical, kind, units);
  return text.length === 0 ? '' : `${text} ${ratioUnitLabel(kind, units)}`;
}

/**
 * A typed ratio back to its canonical value.
 *
 * Bare numbers only — `1.5 qt/lb` typed into a L/kg field is a sentence, not a
 * suffix, and pretending to understand it would be worse than asking again.
 */
export function parseRatio(input: string, kind: RatioKind, units: UnitPreferences): ParseResult {
  const spec = RATIOS[kind];
  const text = input.trim().replace(/,/g, '');
  if (text.length === 0) return { ok: false, error: 'Enter a number.' };
  const value = Number(text);
  if (!Number.isFinite(value)) return { ok: false, error: `"${input.trim()}" is not a number.` };
  return { ok: true, value: spec.us(units) ? value / spec.factor : value };
}

/**
 * Mash thickness in the unit a brewer actually recites: L/kg, or qt/lb when
 * both mass and volume are US.
 */
export function formatMashThickness(lPerKg: number, units: UnitPreferences): string {
  return formatRatioWithUnit(lPerKg, 'volumePerGrainMass', units);
}

/**
 * The unit a *small* volume reads best in — a priming dose, a starter's acid,
 * a blend measured into a jug.
 *
 * `formatQuantity(0.05, 'volume', us)` is "0.01", which is not a measurement
 * anybody can act on; the same volume is 1.7 fl oz, which is.
 */
export function smallVolumeUnit(units: UnitPreferences): UnitKey {
  return units.volume === 'us' ? 'floz' : 'ml';
}

/**
 * The unit a computed volume of any size reads best in: mL under a liter,
 * and for US volumes fl oz under a quart and quarts under a gallon.
 *
 * For a figure the brewer didn't type, which can come out at 30 mL or 30 L
 * from the same block, so neither the big unit nor {@link smallVolumeUnit}
 * suits it every time.
 */
export function scaledVolumeUnit(litres: number, units: UnitPreferences): UnitKey {
  const size = Math.abs(litres);
  if (units.volume === 'us') {
    if (size < quartsToLitres(1)) return 'floz';
    if (size < gallonsToLitres(1)) return 'qt';
    return 'gal';
  }
  return size < 1 ? 'ml' : 'l';
}

/**
 * Split a mass into whole pounds plus remaining ounces, rounded to 0.1 oz so
 * a 1.5 lb addition reads `1 lb 8 oz` rather than `1 lb 8.000 oz`. Tenths of
 * an ounce are rounded as a single integer so 15.95 oz carries to the next
 * pound instead of displaying as `0 lb 16 oz`.
 */
function formatPoundsOunces(kg: number): string {
  if (!Number.isFinite(kg)) return '';
  const sign = kg < 0 ? '-' : '';
  // Round as tenths of an ounce so 15.95 oz carries to the next pound.
  const tenths = Math.round(Math.abs(kgToOunces(kg)) * 10);
  const pounds = Math.floor(tenths / 160);
  const ounces = (tenths - pounds * 160) / 10;
  const ounceText = trimZeros(ounces.toFixed(1));
  if (pounds === 0 && ounces === 0) return `${sign}0 lb`;
  if (pounds === 0) return `${sign}${ounceText} oz`;
  if (ounces === 0) return `${sign}${pounds} lb`;
  return `${sign}${pounds} lb ${ounceText} oz`;
}

/** `4.500` → `4.5`, `4.000` → `4`. */
export function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
}

/**
 * Does this mass render as ounces alone under `lbOz`?
 *
 * The same tenths arithmetic {@link formatPoundsOunces} uses, so the two cannot
 * disagree about where the pound boundary falls. `tenths > 0` excludes an empty
 * field, which prints "0 lb" and should stay pounds.
 */
function rendersAsOuncesOnly(kg: number): boolean {
  if (!Number.isFinite(kg)) return false;
  const tenths = Math.round(Math.abs(kgToOunces(kg)) * 10);
  return tenths > 0 && tenths < 160;
}

/**
 * The unit a bare number should be read in, given what the field is showing.
 *
 * Everywhere except US bulk mass this is just the kind's display unit, because
 * that unit does not move. `lbOz` is the exception: it is the one format whose
 * unit depends on the value, so a cell holding half a pound shows "8 oz" while
 * `displayUnit('mass')` still says `lb`. Clearing that cell and typing `12`
 * meant twelve *pounds* — a 24× jump from what was on screen a moment earlier,
 * with nothing in the field to suggest the unit had changed underneath.
 *
 * Reading the bare number in the unit the field was displaying keeps the rule
 * learnable: a number on its own means whatever the cell already meant. A
 * brewer who wants the other unit types it, which has always worked and is why
 * `12 lb` is still twelve pounds here.
 *
 * Deliberately not inferred from the magnitude of what was typed. "Under a
 * pound means ounces" would make `12` ounces and `2` pounds in the same cell,
 * which is not a rule anyone can hold in their head.
 */
export function bareUnitFor(
  current: number | null,
  kind: MeasureKind,
  units: UnitPreferences,
  options: FormatQuantityOptions = {},
): UnitKey {
  const fallback = options.unit ?? displayUnit(kind, units);
  if (kind !== 'mass' || units.mass !== 'us' || options.massFormat !== 'lbOz') return fallback;
  if (current === null) return fallback;
  return rendersAsOuncesOnly(current) ? 'oz' : fallback;
}

/**
 * The on-screen keyboard for a field that parses a bulk mass. US amounts are
 * routinely typed with their unit ("4 oz", "1 lb 8 oz") and the decimal keypad
 * has no letters. Metric gets the keypad, since a bare decimal covers grams.
 */
export function massInputMode(units: UnitPreferences): 'text' | 'decimal' {
  return units.mass === 'us' ? 'text' : 'decimal';
}

/** What {@link parseQuantity} returns. */
export type ParseResult =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly error: string };

/**
 * A typed string → a canonical value.
 *
 * A bare number is read in the kind's *display* unit; a recognised suffix wins
 * over the preference, so a metric brewer can still type `1.5oz` off an
 * American recipe. A suffix from the wrong family (`500g` in a volume cell) is
 * rejected rather than silently ignored.
 */
export function parseQuantity(
  input: string,
  kind: MeasureKind,
  units: UnitPreferences,
  options: Pick<FormatQuantityOptions, 'unit'> = {},
): ParseResult {
  const text = input.trim().toLowerCase().replace(/,/g, '');
  if (text.length === 0) return { ok: false, error: 'Enter a number.' };

  const fallback = options.unit ?? displayUnit(kind, units);
  if (FAMILY[fallback] === 'mass') {
    const compound = parseCompoundLbOz(text);
    if (compound !== null) return { ok: true, value: compound };
  }

  const match = /^([+-]?(?:\d+\.?\d*|\.\d+))\s*(.*)$/.exec(text);
  if (match === null) return { ok: false, error: `"${input.trim()}" is not a number.` };

  const magnitude = Number(match[1]);
  const suffix = (match[2] ?? '').trim();
  if (!Number.isFinite(magnitude))
    return { ok: false, error: `"${input.trim()}" is not a number.` };

  if (suffix.length === 0) {
    return { ok: true, value: UNITS[fallback].toCanonical(magnitude) };
  }

  const found = SUFFIXES.find(([token]) => token === suffix);
  if (found === undefined) return { ok: false, error: `Unknown unit "${suffix}".` };
  const unit = found[1];
  if (FAMILY[unit] !== FAMILY[fallback]) {
    return { ok: false, error: `"${suffix}" is not a ${FAMILY[fallback]} unit.` };
  }
  return { ok: true, value: UNITS[unit].toCanonical(magnitude) };
}

/** `1 lb 8 oz` → kilograms, or `null` when the string is not that shape. */
function parseCompoundLbOz(text: string): number | null {
  const match = COMPOUND_LB_OZ.exec(text);
  if (match === null) return null;
  const pounds = Number(match[1]);
  const ounces = Number(match[2]);
  if (!Number.isFinite(pounds) || !Number.isFinite(ounces)) return null;
  return poundsToKg(pounds) + ouncesToKg(ounces);
}
